/* =====================================================================
 * serializeWorkbook — host-neutral XLSX workbook model → xlsx ArrayBuffer
 *
 * UI나 DocAgent envelope에 의존하지 않고, 공유 workbook model을 OOXML
 * ArrayBuffer로 직렬화한다. SheetJS가 지원하지 않는 worksheet 기능은 표준 XML
 * 순서를 보존하며 ZIP 레벨로 후주입한다.
 *
 * 설계 메모(속도/메모리):
 *   - 렌더 방향은 렌더 라이브러리(`xlsx-js-style`)로 styles.xml/cellXfs 를 만들고,
 *     ExcelRenderer 가 s-index → CellXf 하이브리드로 정확히 복원한다.
 *   - 데이터 유효성(셀 드롭다운)은 SheetJS write 가 직렬화하지 않으므로,
 *     IR→바이트 단계에서 표준 `<dataValidations>` XML 을 ZIP 레벨로 주입한다.
 *   - 저장 방향은 바이트를 다시 파싱하지 않는다. `XLSX.read` 의 cell.s 는 fill
 *     위주로 축약돼 폰트/정렬이 유실되므로, ExcelRenderer 가 이미 해석해둔
 *     in-memory 스타일을 스냅샷으로 받아 IR 로 매핑한다.
 *   - 필터/정렬은 "뷰 레이어"라 셀을 바꾸지 않는다. 따라서 스냅샷→IR 시
 *     스냅샷이 명시하지 않은 메타(데이터 유효성·오토필터·조건부서식·틀고정·
 *     치수)는 prev IR 에서 그대로 보존해 손실/재파싱을 피한다. 스냅샷이
 *     해당 필드를 실으면(사용자가 편집) 전체 교체한다(replace-or-preserve).
 * ===================================================================== */

import XLSX from 'xlsx-js-style';
import JSZip from 'jszip';

import type {
  XlsxAutoFilterColumn,
  XlsxAutoFilterCustom,
  XlsxCellValue,
  XlsxSheet,
  XlsxWorkbookModel,
} from '../model/types';

const setXmlAttribute = (attributes: string, name: string, value: string): string => {
  const pattern = new RegExp(`\\s${name}\\s*=\\s*(["'])[^"']*\\1`);
  return pattern.test(attributes)
    ? attributes.replace(pattern, ` ${name}="${value}"`)
    : `${attributes} ${name}="${value}"`;
};

/* ------------------------------------------------------------------ */
/* 스타일 매핑 (IR ↔ xlsx-js-style cell.s)                              */
/* ------------------------------------------------------------------ */

type IrCellStyle = NonNullable<XlsxCellValue['style']>;

/** "#RRGGBB" → "RRGGBB" (xlsx-js-style rgb 표기). 앞의 # 제거, 대문자. */
function hexToRgb(hex: string | null | undefined): string | undefined {
  if (!hex) return undefined;
  const h = hex.replace('#', '').trim();
  if (!h) return undefined;
  return h.toUpperCase();
}

/** IR 스타일 → xlsx-js-style cell.s (없으면 undefined). */
export function irStyleToCellS(style: IrCellStyle | null | undefined): Record<string, unknown> | undefined {
  if (!style) return undefined;
  const s: Record<string, unknown> = {};

  const font: Record<string, unknown> = {};
  if (style.bold) font.bold = true;
  if (style.italic) font.italic = true;
  if (style.underline) font.underline = true;
  if (style.strike) font.strike = true;
  if (typeof style.fontSize === 'number' && style.fontSize > 0) font.sz = style.fontSize;
  if (style.fontFamily) font.name = style.fontFamily;
  const fontRgb = hexToRgb(style.color);
  if (fontRgb) font.color = { rgb: fontRgb };
  if (Object.keys(font).length > 0) s.font = font;

  const bgRgb = hexToRgb(style.backgroundColor);
  if (bgRgb) s.fill = { patternType: 'solid', fgColor: { rgb: bgRgb } };

  const alignment: Record<string, unknown> = {};
  if (style.align) alignment.horizontal = style.align;
  if (style.verticalAlign) {
    alignment.vertical = style.verticalAlign === 'middle' ? 'center' : style.verticalAlign;
  }
  if (style.wrapText !== undefined) alignment.wrapText = style.wrapText;
  if (
    typeof style.textRotation === 'number'
    && Number.isFinite(style.textRotation)
    && style.textRotation >= 0
    && style.textRotation <= 255
  ) {
    alignment.textRotation = style.textRotation;
  }
  if (typeof style.indent === 'number' && Number.isFinite(style.indent) && style.indent >= 0) {
    alignment.indent = style.indent;
  }
  // xlsx-js-style uses this key for style de-duplication but currently omits
  // the XML attribute. `injectShrinkToFitStyles` patches the selected xf nodes.
  if (style.shrinkToFit) alignment.shrinkToFit = true;
  if (Object.keys(alignment).length > 0) s.alignment = alignment;

  if (style.numFmt) s.numFmt = style.numFmt;

  // 테두리 — IR side(style+color) → xlsx-js-style border. styles.xml 로 직렬화되어
  // ExcelRenderer 가 다시 파싱(parseBordersFromZip) → CSS 로 복원하므로 라운드트립된다.
  if (style.borders) {
    const sideToCell = (side: { style?: string; color?: string | null } | null | undefined) => {
      if (!side || !side.style) return undefined;
      const out: Record<string, unknown> = { style: side.style };
      const rgb = hexToRgb(side.color);
      if (rgb) out.color = { rgb };
      return out;
    };
    const border: Record<string, unknown> = {};
    const top = sideToCell(style.borders.top);
    const bottom = sideToCell(style.borders.bottom);
    const left = sideToCell(style.borders.left);
    const right = sideToCell(style.borders.right);
    if (top) border.top = top;
    if (bottom) border.bottom = bottom;
    if (left) border.left = left;
    if (right) border.right = right;
    if (Object.keys(border).length > 0) s.border = border;
  }

  return Object.keys(s).length > 0 ? s : undefined;
}

/* ------------------------------------------------------------------ */
/* 열 문자 → 인덱스                                                     */
/* ------------------------------------------------------------------ */

function letterToCol0(letter: string): number {
  let result = 0;
  for (let i = 0; i < letter.length; i += 1) {
    result = result * 26 + (letter.charCodeAt(i) - 64);
  }
  return result - 1;
}

/* ------------------------------------------------------------------ */
/* IR → workbook                                                        */
/* ------------------------------------------------------------------ */

function sheetDims(sheet: XlsxSheet): { rows: number; cols: number } {
  let maxRow = Math.max(1, sheet.rowCount || 0);
  let maxCol = Math.max(1, sheet.colCount || 0);
  for (const a1 of Object.keys(sheet.cells)) {
    const { r, c } = XLSX.utils.decode_cell(a1);
    if (r + 1 > maxRow) maxRow = r + 1;
    if (c + 1 > maxCol) maxCol = c + 1;
  }
  return { rows: maxRow, cols: maxCol };
}

/** IR sheet → xlsx-js-style worksheet. */
function irSheetToWorksheet(sheet: XlsxSheet): XLSX.WorkSheet {
  const ws: Record<string, any> = {};
  const { rows, cols } = sheetDims(sheet);

  for (const [a1, cell] of Object.entries(sheet.cells)) {
    if (!cell) continue;
    const out: Record<string, unknown> = {};
    const hasFormula = !!cell.formula && cell.formula.trim() !== '';
    if (hasFormula) {
      out.f = cell.formula!.replace(/^=/, '');
      if (typeof cell.value === 'boolean') {
        out.v = cell.value;
        out.t = 'b';
      } else {
        const num = typeof cell.value === 'number' ? cell.value : Number(cell.value);
        if (cell.value !== null && cell.value !== undefined && !Number.isNaN(num) && cell.value !== '') {
          out.v = typeof cell.value === 'number' ? cell.value : num;
          out.t = 'n';
        } else {
          out.v = cell.value ?? '';
          out.t = 's';
        }
      }
    } else if (cell.value !== null && cell.value !== undefined) {
      if (typeof cell.value === 'number') { out.v = cell.value; out.t = 'n'; }
      else if (typeof cell.value === 'boolean') { out.v = cell.value; out.t = 'b'; }
      else { out.v = cell.value; out.t = 's'; }
    } else {
      // 값/수식 없이 스타일만 있는 셀 — 빈 문자열로 보존
      out.v = ''; out.t = 's';
    }
    const cs = irStyleToCellS(cell.style);
    if (cs) out.s = cs;
    ws[a1] = out;
  }

  ws['!ref'] = `A1:${XLSX.utils.encode_cell({ r: rows - 1, c: cols - 1 })}`;

  if (Array.isArray(sheet.merges) && sheet.merges.length > 0) {
    ws['!merges'] = sheet.merges
      .map((m) => {
        try { return XLSX.utils.decode_range(m.range); } catch { return null; }
      })
      .filter((r): r is XLSX.Range => r !== null);
  }

  // 열 너비(openpyxl char width) → !cols[c].wch. ExcelRenderer 는 width/wch 를 px 로 환산.
  if (
    (sheet.columnWidths && Object.keys(sheet.columnWidths).length > 0)
    || (sheet.hiddenColumns && Object.keys(sheet.hiddenColumns).length > 0)
  ) {
    const colsArr: Array<Record<string, number | boolean>> = [];
    for (const [key, width] of Object.entries(sheet.columnWidths ?? {})) {
      const c = /^[A-Za-z]+$/.test(key) ? letterToCol0(key.toUpperCase()) : (parseInt(key, 10) || 0);
      if (c < 0) continue;
      while (colsArr.length <= c) colsArr.push({});
      colsArr[c] = { wch: Number(width) };
    }
    for (const [key, hidden] of Object.entries(sheet.hiddenColumns ?? {})) {
      if (!hidden || !/^[A-Za-z]+$/.test(key)) continue;
      const c = letterToCol0(key.toUpperCase());
      if (c < 0) continue;
      while (colsArr.length <= c) colsArr.push({});
      colsArr[c] = { ...colsArr[c], hidden: true };
    }
    if (colsArr.length > 0) ws['!cols'] = colsArr;
  }

  // 행 높이(points) → !rows[r].hpt. ExcelRenderer 는 hpt 를 px 로 환산.
  if (
    (sheet.rowHeights && Object.keys(sheet.rowHeights).length > 0)
    || (sheet.hiddenRows && Object.keys(sheet.hiddenRows).length > 0)
  ) {
    const rowsArr: Array<Record<string, number | boolean>> = [];
    for (const [key, height] of Object.entries(sheet.rowHeights ?? {})) {
      const r = (parseInt(key, 10) || 1) - 1; // 1-based → 0-based
      if (r < 0) continue;
      while (rowsArr.length <= r) rowsArr.push({});
      rowsArr[r] = { hpt: Number(height) };
    }
    for (const [key, hidden] of Object.entries(sheet.hiddenRows ?? {})) {
      if (!hidden || !/^\d+$/.test(key)) continue;
      const r = parseInt(key, 10) - 1;
      if (r < 0) continue;
      while (rowsArr.length <= r) rowsArr.push({});
      rowsArr[r] = { ...rowsArr[r], hidden: true };
    }
    if (rowsArr.length > 0) ws['!rows'] = rowsArr;
  }

  // 오토필터 — SheetJS write 가 직렬화한다.
  if (sheet.autoFilter?.ref) {
    ws['!autofilter'] = { ref: sheet.autoFilter.ref };
  }

  return ws as XLSX.WorkSheet;
}

/** IR → xlsx-js-style WorkBook. */
function irToWorkbook(content: XlsxWorkbookModel): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  const sheets = content.sheets.length > 0 ? content.sheets : [];
  for (const sheet of sheets) {
    const ws = irSheetToWorksheet(sheet);
    XLSX.utils.book_append_sheet(wb, ws, (sheet.name || 'Sheet').slice(0, 31));
  }
  if (wb.SheetNames.length === 0) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['']]), 'Sheet1');
  }

  return wb;
}

function shrinkToFitCellRefs(sheet: XlsxSheet): Set<string> {
  const refs = new Set<string>();
  for (const [address, cell] of Object.entries(sheet.cells)) {
    if (cell?.style?.shrinkToFit === true) {
      refs.add(address.replace(/\$/g, '').toUpperCase());
    }
  }
  return refs;
}

function xmlAttributeValue(attributes: string, name: string): string | undefined {
  const match = new RegExp(`(?:^|\\s)${name}\\s*=\\s*(["'])(.*?)\\1`).exec(attributes);
  return match?.[2];
}

function addShrinkToFitToCellXf(xfXml: string): string {
  const opening = /^<xf\b([^>]*?)(\/?)>/.exec(xfXml);
  if (!opening) return xfXml;
  const xfAttributes = setXmlAttribute(opening[1], 'applyAlignment', '1');
  if (opening[2] === '/') {
    return `<xf${xfAttributes}><alignment shrinkToFit="1"/></xf>`;
  }

  let updated = xfXml.replace(opening[0], `<xf${xfAttributes}>`);
  const alignment = /<alignment\b([^>]*?)(\/?)>/.exec(updated);
  if (alignment) {
    const attributes = setXmlAttribute(alignment[1], 'shrinkToFit', '1');
    return updated.replace(alignment[0], `<alignment${attributes}${alignment[2]}>`);
  }
  return updated.replace('</xf>', '<alignment shrinkToFit="1"/></xf>');
}

/**
 * xlsx-js-style deliberately creates a distinct cellXf for a style containing
 * `alignment.shrinkToFit`, but its XML writer omits that one attribute. Resolve
 * the affected xf indices from the generated worksheets and patch only those
 * style records, so neighbouring cells with otherwise identical styles stay
 * unchanged.
 */
async function injectShrinkToFitStyles(
  buf: ArrayBuffer,
  content: XlsxWorkbookModel,
): Promise<ArrayBuffer> {
  if (!content.sheets.some((sheet) => shrinkToFitCellRefs(sheet).size > 0)) return buf;
  try {
    const zip = await JSZip.loadAsync(buf);
    const styleIndices = new Set<number>();
    for (let index = 0; index < content.sheets.length; index += 1) {
      const refs = shrinkToFitCellRefs(content.sheets[index]);
      if (refs.size === 0) continue;
      const worksheet = zip.file(`xl/worksheets/sheet${index + 1}.xml`);
      if (!worksheet) continue;
      const xml = await worksheet.async('text');
      for (const match of xml.matchAll(/<c\b([^>]*)>/g)) {
        const address = xmlAttributeValue(match[1], 'r')?.replace(/\$/g, '').toUpperCase();
        if (!address || !refs.has(address)) continue;
        const rawStyleIndex = xmlAttributeValue(match[1], 's');
        const styleIndex = rawStyleIndex === undefined ? NaN : Number(rawStyleIndex);
        if (Number.isInteger(styleIndex) && styleIndex >= 0) styleIndices.add(styleIndex);
      }
    }
    if (styleIndices.size === 0) return buf;

    const stylesFile = zip.file('xl/styles.xml');
    if (!stylesFile) return buf;
    const original = await stylesFile.async('text');
    const cellXfs = /<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/.exec(original);
    if (!cellXfs) return buf;
    let currentIndex = -1;
    const body = cellXfs[1].replace(
      /<xf\b[^>]*?(?:\/>|>[\s\S]*?<\/xf>)/g,
      (xfXml) => {
        currentIndex += 1;
        return styleIndices.has(currentIndex) ? addShrinkToFitToCellXf(xfXml) : xfXml;
      },
    );
    const updated = original.replace(cellXfs[1], body);
    if (updated === original) return buf;
    zip.file('xl/styles.xml', updated);
    return zip.generateAsync({ type: 'arraybuffer' });
  } catch (error) {
    console.warn('[xlsx-engine] shrink-to-fit style injection failed:', error);
    return buf;
  }
}

/**
 * xlsx-js-style does not serialize `Workbook.Views[].activeTab`, so preserve
 * the engine model's active sheet in workbook.xml after SheetJS writes the ZIP.
 */
async function injectActiveSheetIndex(
  buf: ArrayBuffer,
  activeSheetIndex: number | undefined,
  sheetCount: number,
): Promise<ArrayBuffer> {
  try {
    const requested =
      typeof activeSheetIndex === 'number' && Number.isFinite(activeSheetIndex)
        ? Math.trunc(activeSheetIndex)
        : 0;
    const activeTab = Math.min(Math.max(requested, 0), Math.max(sheetCount - 1, 0));
    const zip = await JSZip.loadAsync(buf);
    const workbookFile = zip.file('xl/workbook.xml');
    if (!workbookFile) return buf;

    let xml = await workbookFile.async('text');
    const workbookView = xml.match(/<workbookView\b[^>]*>/)?.[0];
    if (workbookView) {
      const activeTabAttribute = /\sactiveTab\s*=\s*(["'])[^"']*\1/;
      const updatedView = activeTabAttribute.test(workbookView)
        ? workbookView.replace(activeTabAttribute, ` activeTab="${activeTab}"`)
        : workbookView.replace(/\/?\s*>$/, (closing) => ` activeTab="${activeTab}"${closing}`);
      xml = xml.replace(workbookView, updatedView);
    } else if (/<bookViews\b[^>]*>/.test(xml)) {
      xml = xml.replace(
        /<bookViews\b[^>]*>/,
        (opening) => `${opening}<workbookView activeTab="${activeTab}"/>`,
      );
    } else if (/<sheets\b/.test(xml)) {
      // CT_Workbook schema order places bookViews immediately before sheets.
      xml = xml.replace(
        /<sheets\b/,
        `<bookViews><workbookView activeTab="${activeTab}"/></bookViews><sheets`,
      );
    } else {
      return buf;
    }

    zip.file('xl/workbook.xml', xml);
    return await zip.generateAsync({ type: 'arraybuffer' });
  } catch (err) {
    console.warn('[xlsx-engine] active sheet injection failed:', err);
    return buf;
  }
}

/* ------------------------------------------------------------------ */
/* 데이터 유효성(<dataValidations>) XML 빌드 + 주입                     */
/* ------------------------------------------------------------------ */

function xmlEscape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * IR 데이터 유효성 1개 → `<dataValidation>` XML. list 면 formula1 보강.
 * 비교형 유효성(whole/decimal/date/textLength 등)의 operator/formula2 와
 * 입력 안내(prompt)/오류(error) 메시지도 함께 방출해 왕복 손실을 막는다.
 */
function dvToXml(dv: NonNullable<XlsxSheet['dataValidations']>[number]): string | null {
  const sqref = dv.range?.trim();
  if (!sqref) return null;
  const type = dv.type || 'list';

  let formula1 = dv.formula1 ?? null;
  if ((!formula1 || formula1.trim() === '') && type === 'list' && Array.isArray(dv.allowedValues) && dv.allowedValues.length > 0) {
    // 정적 후보 → "v1,v2,v3" (콤마 포함 값은 Excel 자체 제약상 분리됨)
    formula1 = `"${dv.allowedValues.join(',')}"`;
  }
  if (formula1 && formula1.startsWith('=')) formula1 = formula1.slice(1);
  let formula2 = dv.formula2 ?? null;
  if (formula2 && formula2.trim() === '') formula2 = null;
  if (formula2 && formula2.startsWith('=')) formula2 = formula2.slice(1);

  const attrs = [`type="${xmlEscape(type)}"`];
  // between 등 비교 연산자 — 미지정 시 OOXML 기본값(between)이므로 생략 가능
  if (dv.operator) attrs.push(`operator="${xmlEscape(dv.operator)}"`);
  attrs.push(`allowBlank="${dv.allowBlank ? '1' : '0'}"`);
  attrs.push('showInputMessage="1"', 'showErrorMessage="1"');
  if (dv.promptTitle) attrs.push(`promptTitle="${xmlEscape(dv.promptTitle)}"`);
  if (dv.prompt) attrs.push(`prompt="${xmlEscape(dv.prompt)}"`);
  if (dv.errorTitle) attrs.push(`errorTitle="${xmlEscape(dv.errorTitle)}"`);
  if (dv.error) attrs.push(`error="${xmlEscape(dv.error)}"`);
  attrs.push(`sqref="${xmlEscape(sqref)}"`);

  const inner =
    (formula1 != null ? `<formula1>${xmlEscape(formula1)}</formula1>` : '') +
    (formula2 != null ? `<formula2>${xmlEscape(formula2)}</formula2>` : '');
  return `<dataValidation ${attrs.join(' ')}>${inner}</dataValidation>`;
}

/** sheet 의 dataValidations → `<dataValidations count=..>..</dataValidations>` (없으면 ''). */
function sheetDvContainerXml(sheet: XlsxSheet): string {
  const list = sheet.dataValidations;
  if (!Array.isArray(list) || list.length === 0) return '';
  const parts = list.map(dvToXml).filter((x): x is string => !!x);
  if (parts.length === 0) return '';
  return `<dataValidations count="${parts.length}">${parts.join('')}</dataValidations>`;
}

/** dataValidations 뒤에 와야 하는 요소들(이 중 문서상 가장 앞선 위치 직전에 삽입). */
const AFTER_ANCHORS = [
  '<hyperlinks', '<printOptions', '<pageMargins', '<pageSetup', '<headerFooter',
  '<rowBreaks', '<colBreaks', '<customProperties', '<cellWatches', '<ignoredErrors',
  '<smartTags', '<drawing', '<legacyDrawing', '<picture', '<oleObjects', '<controls',
  '<webPublishItems', '<tableParts', '<extLst', '</worksheet>',
];

/**
 * 출력 ZIP 의 각 시트 XML 에 표준 `<dataValidations>` 블록을 OOXML 스키마
 * 위치에 주입한다. SheetJS write 는 시트를 SheetNames 순서로 sheet1.xml,
 * sheet2.xml 로 쓰므로 인덱스로 직접 타겟팅한다.
 */
async function injectDataValidations(buf: ArrayBuffer, content: XlsxWorkbookModel): Promise<ArrayBuffer> {
  const needs = content.sheets.some((s) => sheetDvContainerXml(s) !== '');
  if (!needs) return buf;
  try {
    const zip = await JSZip.loadAsync(buf);
    let mutated = false;
    for (let i = 0; i < content.sheets.length; i++) {
      const dvXml = sheetDvContainerXml(content.sheets[i]);
      if (!dvXml) continue;
      const path = `xl/worksheets/sheet${i + 1}.xml`;
      const file = zip.file(path);
      if (!file) continue;
      let xml = await file.async('text');
      if (xml.includes('<dataValidations')) continue;
      let insertAt = -1;
      for (const anchor of AFTER_ANCHORS) {
        const idx = xml.indexOf(anchor);
        if (idx !== -1 && (insertAt === -1 || idx < insertAt)) insertAt = idx;
      }
      if (insertAt === -1) continue;
      xml = xml.slice(0, insertAt) + dvXml + xml.slice(insertAt);
      zip.file(path, xml);
      mutated = true;
    }
    if (!mutated) return buf;
    return await zip.generateAsync({ type: 'arraybuffer' });
  } catch (err) {
    console.warn('[xlsx-engine] dataValidation injection failed:', err);
    return buf;
  }
}

/* ------------------------------------------------------------------ */
/* 틀 고정(freeze panes) — <sheetView><pane/> 주입                      */
/*                                                                     */
/* SheetJS write 는 틀 고정을 직렬화하지 않으므로, sheetView 에 <pane> 을    */
/* 삽입해 다운로드/재로드 시 고정이 보존되게 한다.                          */
/* ------------------------------------------------------------------ */

/** IR freeze → `<pane/><selection/>` XML (없으면 ''). */
function freezePaneXml(freeze: XlsxSheet['freeze']): string {
  if (!freeze) return '';
  const rows = Math.max(0, Math.trunc(freeze.rows || 0));
  const cols = Math.max(0, Math.trunc(freeze.cols || 0));
  if (rows === 0 && cols === 0) return '';
  const topLeft = XLSX.utils.encode_cell({ r: rows, c: cols });
  const activePane = cols > 0 && rows > 0 ? 'bottomRight' : cols > 0 ? 'topRight' : 'bottomLeft';
  const attrs: string[] = [];
  if (cols > 0) attrs.push(`xSplit="${cols}"`);
  if (rows > 0) attrs.push(`ySplit="${rows}"`);
  attrs.push(`topLeftCell="${topLeft}"`, `activePane="${activePane}"`, 'state="frozen"');
  return `<pane ${attrs.join(' ')}/><selection pane="${activePane}" activeCell="${topLeft}" sqref="${topLeft}"/>`;
}

/** 각 시트의 sheetView 에 freeze pane 을 주입한다(없으면 무시). */
async function injectFreezePanes(buf: ArrayBuffer, content: XlsxWorkbookModel): Promise<ArrayBuffer> {
  const needs = content.sheets.some((s) => freezePaneXml(s.freeze) !== '');
  if (!needs) return buf;
  try {
    const zip = await JSZip.loadAsync(buf);
    let mutated = false;
    for (let i = 0; i < content.sheets.length; i++) {
      const paneXml = freezePaneXml(content.sheets[i].freeze);
      if (!paneXml) continue;
      const path = `xl/worksheets/sheet${i + 1}.xml`;
      const file = zip.file(path);
      if (!file) continue;
      let xml = await file.async('text');
      if (xml.includes('<pane ')) continue;
      // SheetJS 는 보통 <sheetView ... workbookViewId="0"/> 자기닫힘으로 쓴다.
      const selfClose = xml.match(/<sheetView\b[^>]*\/>/);
      if (selfClose) {
        const open = selfClose[0].replace(/\/>$/, '>');
        xml = xml.replace(selfClose[0], `${open}${paneXml}</sheetView>`);
        mutated = true;
      } else {
        const openMatch = xml.match(/<sheetView\b[^>]*>/);
        if (!openMatch) continue;
        const idx = (openMatch.index ?? 0) + openMatch[0].length;
        xml = xml.slice(0, idx) + paneXml + xml.slice(idx);
        mutated = true;
      }
      zip.file(path, xml);
    }
    if (!mutated) return buf;
    return await zip.generateAsync({ type: 'arraybuffer' });
  } catch (err) {
    console.warn('[xlsx-engine] freeze pane injection failed:', err);
    return buf;
  }
}

/* ------------------------------------------------------------------ */
/* 행 개요(outline) 레벨 — <row outlineLevel=..> + <sheetPr><outlinePr> 주입 */
/*                                                                     */
/* SheetJS write 는 outlineLevel 을 직렬화하지 않는다. 부분합이 부여한       */
/* rowOutlineLevels 를 해당 <row> 태그에 후주입해 Excel 그룹(개요)으로        */
/* 표시되게 한다. 값 없는(=셀이 없어 <row> 태그가 없는) 행은 best-effort 로   */
/* 건너뛴다. sheetPr 은 OOXML 상 worksheet 최상단 자식이므로 여는 태그       */
/* 직후에 삽입한다.                                                        */
/* ------------------------------------------------------------------ */

/** 시트의 유효한 개요 행 목록(1-based 행 번호 → 레벨 1~7). 0/부재는 제외. */
function sheetOutlineRows(sheet: XlsxSheet): Array<{ row: number; level: number }> {
  const lv = sheet.rowOutlineLevels;
  if (!lv) return [];
  const out: Array<{ row: number; level: number }> = [];
  for (const [key, level] of Object.entries(lv)) {
    const r = parseInt(key, 10);
    const l = Math.trunc(Number(level));
    if (!Number.isInteger(r) || r < 1) continue;
    if (!Number.isFinite(l) || l < 1) continue; // 0/부재 = 최상위(그룹 없음)
    out.push({ row: r, level: Math.min(l, 7) });
  }
  return out;
}

async function injectRowOutlineLevels(
  buf: ArrayBuffer,
  content: XlsxWorkbookModel,
): Promise<ArrayBuffer> {
  const needs = content.sheets.some((s) => sheetOutlineRows(s).length > 0);
  if (!needs) return buf;
  try {
    const zip = await JSZip.loadAsync(buf);
    let mutated = false;
    for (let i = 0; i < content.sheets.length; i++) {
      const rows = sheetOutlineRows(content.sheets[i]);
      if (rows.length === 0) continue;
      const path = `xl/worksheets/sheet${i + 1}.xml`;
      const file = zip.file(path);
      if (!file) continue;
      let xml = await file.async('text');
      const levelByRow = new Map<number, number>();
      for (const { row, level } of rows) levelByRow.set(row, level);
      let injected = 0;
      // 각 <row r="N" ...> 에 outlineLevel 속성 추가(이미 있으면 유지).
      xml = xml.replace(/<row\b([^>]*?)(\/?)>/g, (full, attrs: string, selfClose: string) => {
        const m = /\br="(\d+)"/.exec(attrs);
        if (!m) return full;
        const level = levelByRow.get(parseInt(m[1], 10));
        if (!level) return full;
        if (/\boutlineLevel=/.test(attrs)) return full; // 이미 존재 — 보존
        injected += 1;
        return `<row${attrs} outlineLevel="${level}"${selfClose}>`;
      });
      if (injected === 0) continue; // 대상 <row> 없음 — best-effort 스킵
      // <sheetPr><outlinePr/> — worksheet 최상단 자식(없을 때만 주입).
      if (!xml.includes('<sheetPr')) {
        xml = xml.replace(
          /(<worksheet\b[^>]*>)/,
          '$1<sheetPr><outlinePr summaryBelow="1" summaryRight="1"/></sheetPr>',
        );
      }
      zip.file(path, xml);
      mutated = true;
    }
    if (!mutated) return buf;
    return await zip.generateAsync({ type: 'arraybuffer' });
  } catch (err) {
    console.warn('[xlsx-engine] row outline level injection failed:', err);
    return buf;
  }
}

/* ------------------------------------------------------------------ */
/* 오토필터 열 조건(<filterColumn>) 주입                                */
/*                                                                     */
/* SheetJS write 는 `!autofilter` 를 자기닫힘 `<autoFilter ref=".."/>` 로만  */
/* 직렬화한다. IR 이 열별 조건(values/custom)을 실으면 그 요소를 자식 포함     */
/* 형태로 교체해 값 목록/비교 조건이 파일에 왕복되게 한다.                   */
/* ------------------------------------------------------------------ */

/** IR op → OOXML customFilter operator (텍스트 조건은 와일드카드로 별도 처리). */
const IR_OP_TO_OOXML: Record<string, string> = {
  gt: 'greaterThan',
  ge: 'greaterThanOrEqual',
  lt: 'lessThan',
  le: 'lessThanOrEqual',
  eq: 'equal',
  ne: 'notEqual',
};

/**
 * IR 비교 조건 1건 → `<customFilter>` XML. Excel 관례에 따라 텍스트 조건은
 * equal(기본값, operator 생략)/notEqual + 와일드카드 val 로 역변환한다:
 * contains→"*v*", beginsWith→"v*", endsWith→"*v", notContains→notEqual "*v*".
 */
function customFilterXml(c: XlsxAutoFilterCustom): string {
  const v = String(c.value);
  switch (c.op) {
    case 'contains':
      return `<customFilter val="${xmlEscape(`*${v}*`)}"/>`;
    case 'notContains':
      return `<customFilter operator="notEqual" val="${xmlEscape(`*${v}*`)}"/>`;
    case 'beginsWith':
      return `<customFilter val="${xmlEscape(`${v}*`)}"/>`;
    case 'endsWith':
      return `<customFilter val="${xmlEscape(`*${v}`)}"/>`;
    default:
      return `<customFilter operator="${IR_OP_TO_OOXML[c.op] || 'equal'}" val="${xmlEscape(v)}"/>`;
  }
}

/** 열 조건 1개 → `<filterColumn colId>` XML (조건이 없으면 ''). */
function filterColumnXml(col: XlsxAutoFilterColumn): string {
  const parts: string[] = [];
  if (Array.isArray(col.values) && col.values.length > 0) {
    parts.push(`<filters>${col.values.map((v) => `<filter val="${xmlEscape(String(v))}"/>`).join('')}</filters>`);
  }
  if (Array.isArray(col.custom) && col.custom.length > 0) {
    const andAttr = col.and && col.custom.length > 1 ? ' and="1"' : '';
    parts.push(`<customFilters${andAttr}>${col.custom.map(customFilterXml).join('')}</customFilters>`);
  }
  if (parts.length === 0) return '';
  return `<filterColumn colId="${Math.max(0, Math.trunc(col.colId))}">${parts.join('')}</filterColumn>`;
}

/** sheet 의 autoFilter(columns 포함) → `<autoFilter>` XML (조건 없으면 null). */
function sheetAutoFilterXml(sheet: XlsxSheet): string | null {
  const af = sheet.autoFilter;
  if (!af?.ref || !Array.isArray(af.columns) || af.columns.length === 0) return null;
  const cols = af.columns.map(filterColumnXml).filter((x) => x !== '');
  if (cols.length === 0) return null;
  return `<autoFilter ref="${xmlEscape(af.ref)}">${cols.join('')}</autoFilter>`;
}

/**
 * 출력 ZIP 에서 SheetJS 가 쓴 자기닫힘 `<autoFilter ref=".."/>` 를 열 조건이
 * 있는 시트에 한해 자식 포함 형태로 교체한다(injectDataValidations 와 동일한
 * JSZip 패턴, 시트는 인덱스로 타겟팅).
 */
async function injectAutoFilterCriteria(buf: ArrayBuffer, content: XlsxWorkbookModel): Promise<ArrayBuffer> {
  const needs = content.sheets.some((s) => sheetAutoFilterXml(s) !== null);
  if (!needs) return buf;
  try {
    const zip = await JSZip.loadAsync(buf);
    let mutated = false;
    for (let i = 0; i < content.sheets.length; i++) {
      const afXml = sheetAutoFilterXml(content.sheets[i]);
      if (!afXml) continue;
      const path = `xl/worksheets/sheet${i + 1}.xml`;
      const file = zip.file(path);
      if (!file) continue;
      let xml = await file.async('text');
      const selfClose = xml.match(/<autoFilter\b[^>]*\/>/);
      if (!selfClose) continue; // SheetJS 미방출/이미 자식 보유 — 건드리지 않음
      xml = xml.replace(selfClose[0], afXml);
      zip.file(path, xml);
      mutated = true;
    }
    if (!mutated) return buf;
    return await zip.generateAsync({ type: 'arraybuffer' });
  } catch (err) {
    console.warn('[xlsx-engine] autoFilter criteria injection failed:', err);
    return buf;
  }
}

/* ------------------------------------------------------------------ */
/* 조건부 서식(<conditionalFormatting> + styles <dxfs>) 주입             */
/*                                                                     */
/* ExcelRenderer 는 조건부 서식을 파싱·평가해 렌더하므로(parseConditional  */
/* Formats/conditionalStyleMap), 호스트 렌더와 다운로드 모두 */
/* 에 주입한다 — 에이전트가 만든 CF 가 화면에 뜨고 저장/다운로드에 살아남는 */
/* 백본. dxf(채움/글자색/굵게/기울임)와 colorScale, containsText 계열 등    */
/* dxf 기반 규칙 전 타입을 무손실 통과시키고, 재구성 불가능한 규칙          */
/* (dataBar/iconSet 등)만 안전하게 생략한다.                              */
/* ------------------------------------------------------------------ */

/** 직렬화 대상 CF 규칙(IR rule 컨테이너와 구조 호환) — 단위 테스트용으로 export. */
export interface CfRule {
  type: string;
  operator?: string;
  /** containsText 계열(업로드 파일 유래) — 무손실 통과를 위해 유지·방출. */
  text?: string;
  priority?: number;
  formula?: string[] | string;
  /** Legacy parser/IR spelling; used only when canonical formula is absent. */
  formulas?: string[] | string;
  dxf?: { bgColor?: string; fontColor?: string; bold?: boolean; italic?: boolean };
  colorScale?: { cfvo: Array<{ type: string; val?: string }>; colors: string[] };
}

/** "#RRGGBB" → "FFRRGGBB"(ARGB). 8자리면 그대로. */
function toArgb(hex: string | undefined): string {
  const h = (hex || '').replace('#', '').trim().toUpperCase();
  if (h.length === 8) return h;
  if (h.length === 6) return `FF${h}`;
  return 'FF000000';
}

/** dxf(차등서식) → `<dxf>` XML. Excel 형식(bgColor 강조)을 따른다. 테스트용 export. */
export function dxfToXml(dxf: NonNullable<CfRule['dxf']>): string {
  let fontInner = '';
  if (dxf.bold) fontInner += '<b/>';
  // 기울임 — 렌더러 parseDxfs 가 <i/> 를 fontStyle:'italic' 으로 파싱하므로 왕복된다.
  if (dxf.italic) fontInner += '<i/>';
  if (dxf.fontColor) fontInner += `<color rgb="${toArgb(dxf.fontColor)}"/>`;
  const fontXml = fontInner ? `<font>${fontInner}</font>` : '';
  const fillXml = dxf.bgColor
    ? `<fill><patternFill><bgColor rgb="${toArgb(dxf.bgColor)}"/></patternFill></fill>`
    : '';
  return `<dxf>${fontXml}${fillXml}</dxf>`;
}

/** 중복 제거하며 dxf 를 모으고 전역 dxfId 를 부여하는 레지스트리. 테스트용 export. */
export class DxfRegistry {
  private list: string[] = [];
  private map = new Map<string, number>();
  add(dxf: NonNullable<CfRule['dxf']>): number {
    const xml = dxfToXml(dxf);
    const found = this.map.get(xml);
    if (found !== undefined) return found;
    const idx = this.list.length;
    this.list.push(xml);
    this.map.set(xml, idx);
    return idx;
  }
  get count(): number { return this.list.length; }
  toXml(): string {
    if (this.list.length === 0) return '';
    return `<dxfs count="${this.list.length}">${this.list.join('')}</dxfs>`;
  }
}

/** 규칙 1개 → `<cfRule>` XML(재구성 불가하면 null). 테스트용 export.
 * dxf 기반 규칙은 type 을 그대로 방출하므로 containsText/notContainsText/
 * beginsWith/endsWith 등 업로드 유래 텍스트 규칙도 무손실 통과한다. */
export function cfRuleToXml(rule: CfRule, priority: number, dxfReg: DxfRegistry): string | null {
  if (rule.type === 'colorScale' && rule.colorScale) {
    const { cfvo, colors } = rule.colorScale;
    if (!cfvo?.length || !colors?.length) return null;
    const cfvoXml = cfvo
      .map((v) => `<cfvo type="${xmlEscape(v.type)}"${v.val != null ? ` val="${xmlEscape(String(v.val))}"` : ''}/>`)
      .join('');
    const colorXml = colors.map((c) => `<color rgb="${toArgb(c)}"/>`).join('');
    return `<cfRule type="colorScale" priority="${priority}"><colorScale>${cfvoXml}${colorXml}</colorScale></cfRule>`;
  }
  if (rule.dxf) {
    const dxfId = dxfReg.add(rule.dxf);
    const attrs = [`type="${xmlEscape(rule.type)}"`, `dxfId="${dxfId}"`, `priority="${priority}"`];
    if (rule.operator) attrs.push(`operator="${xmlEscape(rule.operator)}"`);
    if (rule.text) attrs.push(`text="${xmlEscape(rule.text)}"`);
    const rawFormulas = rule.formula ?? rule.formulas ?? [];
    const formulaList = Array.isArray(rawFormulas) ? rawFormulas : [rawFormulas];
    const formulas = formulaList
      .map((formula) => `<formula>${xmlEscape(String(formula))}</formula>`)
      .join('');
    return `<cfRule ${attrs.join(' ')}>${formulas}</cfRule>`;
  }
  return null; // dataBar/iconSet 등 — 안전하게 생략
}

/** sheet 의 조건부서식 → sqref 별 `<conditionalFormatting>` 블록(없으면 ''). 테스트용 export.
 * 우선순위 정책: 명시 `rule.priority` 는 그대로 보존한다(우선순위 부여는
 * 저작 다이얼로그/도구의 책임 — 직렬화기 renumber 에 의존하지 않는다).
 * 누락된 규칙만 "명시 최대값 + 1" 부터 backfill 해 중복 번호를 피한다.
 * 단, priority 는 워크북 전역 유일해야 하므로(Excel 규격) 명시값이라도 이미
 * 쓰인 번호면 다음 빈 번호로 밀어낸다 — priority 속성 없이 파싱된(0 부여)
 * 규칙과 신규 규칙이 겹칠 때 중복 priority 로 인한 Excel 복구 프롬프트 방지. */
export function sheetConditionalFormattingXml(sheet: XlsxSheet, dxfReg: DxfRegistry): string {
  const list = sheet.conditionalFormats;
  if (!Array.isArray(list) || list.length === 0) return '';
  const bySqref = new Map<string, string[]>();
  // 명시 우선순위 최대값 스캔 — backfill 시작점(명시값과의 충돌 방지)
  let maxExplicit = 0;
  for (const cf of list) {
    const p = (cf.rule as unknown as CfRule | undefined)?.priority;
    if (typeof p === 'number' && Number.isFinite(p) && p > maxExplicit) maxExplicit = p;
  }
  let prio = maxExplicit + 1;
  const usedPriorities = new Set<number>();
  for (const cf of list) {
    const sqref = (cf.range || '').trim();
    if (!sqref) continue;
    const rule = cf.rule as unknown as CfRule;
    let priority: number;
    if (typeof rule.priority === 'number' && Number.isFinite(rule.priority)) {
      priority = rule.priority;
    } else {
      priority = prio;
      prio += 1; // backfill 한 경우에만 증가 — 명시 규칙과 카운터가 어긋나지 않게
    }
    // 전역 유일성 보장 — 이미 쓰인 번호면 빈 번호로 이동
    while (usedPriorities.has(priority)) priority = prio++;
    usedPriorities.add(priority);
    const xml = cfRuleToXml(rule, priority, dxfReg);
    if (!xml) continue;
    const arr = bySqref.get(sqref) ?? [];
    arr.push(xml);
    bySqref.set(sqref, arr);
  }
  let out = '';
  bySqref.forEach((rules, sqref) => {
    out += `<conditionalFormatting sqref="${xmlEscape(sqref)}">${rules.join('')}</conditionalFormatting>`;
  });
  return out;
}

/** conditionalFormatting 다음에 와야 하는 요소들(가장 앞선 위치 직전에 삽입). */
const CF_AFTER_ANCHORS = ['<dataValidations', ...AFTER_ANCHORS];

/** 출력 ZIP 에 조건부서식(+styles dxfs)을 주입한다(에디터 렌더 + 다운로드 공용).
 * `<conditionalFormatting` 이 이미 있는 시트는 건너뛰므로 이중 주입에 안전하다. */
async function injectConditionalFormats(buf: ArrayBuffer, content: XlsxWorkbookModel): Promise<ArrayBuffer> {
  const needs = content.sheets.some((s) => Array.isArray(s.conditionalFormats) && s.conditionalFormats.length > 0);
  if (!needs) return buf;
  try {
    const zip = await JSZip.loadAsync(buf);
    const dxfReg = new DxfRegistry();
    let mutated = false;
    for (let i = 0; i < content.sheets.length; i++) {
      const cfXml = sheetConditionalFormattingXml(content.sheets[i], dxfReg);
      if (!cfXml) continue;
      const path = `xl/worksheets/sheet${i + 1}.xml`;
      const file = zip.file(path);
      if (!file) continue;
      let xml = await file.async('text');
      if (xml.includes('<conditionalFormatting')) continue;
      let insertAt = -1;
      for (const anchor of CF_AFTER_ANCHORS) {
        const idx = xml.indexOf(anchor);
        if (idx !== -1 && (insertAt === -1 || idx < insertAt)) insertAt = idx;
      }
      if (insertAt === -1) continue;
      xml = xml.slice(0, insertAt) + cfXml + xml.slice(insertAt);
      zip.file(path, xml);
      mutated = true;
    }
    if (mutated && dxfReg.count > 0) {
      const sf = zip.file('xl/styles.xml');
      if (sf) {
        let sx = await sf.async('text');
        const dxfsXml = dxfReg.toXml();
        if (/<dxfs\b[^>]*\/>/.test(sx)) {
          // SheetJS 가 쓰는 빈 self-closing <dxfs count="0"/> 를 교체
          sx = sx.replace(/<dxfs\b[^>]*\/>/, dxfsXml);
        } else if (/<dxfs\b[^>]*>[\s\S]*?<\/dxfs>/.test(sx)) {
          sx = sx.replace(/<dxfs\b[^>]*>[\s\S]*?<\/dxfs>/, dxfsXml);
        } else {
          // dxfs 요소가 없으면 스키마 순서(cellStyles 뒤, tableStyles 앞)에 삽입
          let at = -1;
          for (const anchor of ['<tableStyles', '<colors', '<extLst', '</styleSheet>']) {
            const idx = sx.indexOf(anchor);
            if (idx !== -1 && (at === -1 || idx < at)) at = idx;
          }
          if (at !== -1) sx = sx.slice(0, at) + dxfsXml + sx.slice(at);
        }
        zip.file('xl/styles.xml', sx);
      }
    }
    if (!mutated) return buf;
    return await zip.generateAsync({ type: 'arraybuffer' });
  } catch (err) {
    console.warn('[xlsx-engine] conditional format injection failed:', err);
    return buf;
  }
}

/* ------------------------------------------------------------------ */
/* 하이퍼링크(<hyperlinks>) + 시트 rels 주입                            */
/*                                                                     */
/* SheetJS write 는 셀 하이퍼링크를 직렬화하지 않으므로, IR 셀의          */
/* cell.hyperlink 를 모아 시트 XML 에 `<hyperlinks>` 블록을 넣고, 외부     */
/* URL 은 `xl/worksheets/_rels/sheetN.xml.rels` 에 External 관계로 건다.   */
/* 내부 참조("Sheet1!A1")는 rId 없이 location 속성으로 방출한다.          */
/* ------------------------------------------------------------------ */

const HYPERLINK_REL_TYPE =
  'http://schemas.openxmlformats.org/officeDocument/2006/relationships/hyperlink';
const COMMENTS_REL_TYPE =
  'http://schemas.openxmlformats.org/officeDocument/2006/relationships/comments';
const VML_DRAWING_REL_TYPE =
  'http://schemas.openxmlformats.org/officeDocument/2006/relationships/vmlDrawing';
const COMMENTS_CONTENT_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.comments+xml';

/** <hyperlinks> 는 dataValidations 뒤·pageMargins 앞. 이 앵커들 중 가장 앞선 위치 직전에 삽입. */
const HYPERLINK_AFTER_ANCHORS = [
  '<printOptions', '<pageMargins', '<pageSetup', '<headerFooter',
  '<rowBreaks', '<colBreaks', '<customProperties', '<cellWatches', '<ignoredErrors',
  '<smartTags', '<drawing', '<legacyDrawing', '<picture', '<oleObjects', '<controls',
  '<webPublishItems', '<tableParts', '<extLst', '</worksheet>',
];

/** <legacyDrawing> 뒤에 와야 하는 요소들(코멘트 VML 참조를 이 앞에 삽입). */
const LEGACY_DRAWING_AFTER_ANCHORS = [
  '<legacyDrawingHF', '<picture', '<oleObjects', '<controls',
  '<webPublishItems', '<tableParts', '<extLst', '</worksheet>',
];

/** anchor 목록 중 XML 에서 가장 앞선 위치(없으면 -1). 신규 요소 삽입 지점 탐색용. */
function findInsertPos(xml: string, anchors: string[]): number {
  let insertAt = -1;
  for (const anchor of anchors) {
    const idx = xml.indexOf(anchor);
    if (idx !== -1 && (insertAt === -1 || idx < insertAt)) insertAt = idx;
  }
  return insertAt;
}

/**
 * 하이퍼링크 target 이 외부 URL 인지(rId+rels External 필요) 판정한다.
 * URL 스킴(http/https/ftp/file/mailto/tel...)·프로토콜 상대(//)·www. 는 외부,
 * 그 외("Sheet1!A1" 등 시트 내부 참조·정의된 이름)는 내부 location.
 */
function isExternalHyperlinkTarget(target: string): boolean {
  const t = target.trim();
  if (!t) return false;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(t)) return true; // http:// https:// ftp:// file:// ...
  if (/^(mailto|tel|sip|callto):/i.test(t)) return true; // 스킴만 있는 URL
  if (t.startsWith('//') || t.startsWith('www.')) return true; // 프로토콜 상대/www
  return false;
}

/** 시트 rels XML 에서 다음 사용 가능한 rId 번호(최대값+1, 없으면 1). */
function nextRelIdNum(relsXml: string | null): number {
  if (!relsXml) return 1;
  let max = 0;
  for (const m of relsXml.matchAll(/Id="rId(\d+)"/g)) {
    const n = parseInt(m[1], 10);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return max + 1;
}

/** 관계 XML 조각들을 시트 rels 에 병합(없으면 새 Relationships 문서 생성). */
function mergeSheetRels(relsXml: string | null, relParts: string[]): string {
  if (relParts.length === 0) return relsXml ?? '';
  if (!relsXml) {
    return (
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
      relParts.join('') +
      '</Relationships>'
    );
  }
  const idx = relsXml.lastIndexOf('</Relationships>');
  if (idx === -1) return relsXml;
  return relsXml.slice(0, idx) + relParts.join('') + relsXml.slice(idx);
}

/** 시트의 하이퍼링크 보유 셀 목록(정렬 없이 삽입 순서 유지). */
function sheetHyperlinkCells(sheet: XlsxSheet): Array<[string, NonNullable<XlsxCellValue['hyperlink']>]> {
  const out: Array<[string, NonNullable<XlsxCellValue['hyperlink']>]> = [];
  for (const [a1, cell] of Object.entries(sheet.cells)) {
    if (cell && cell.hyperlink && cell.hyperlink.target && cell.hyperlink.target.trim()) {
      out.push([a1, cell.hyperlink]);
    }
  }
  return out;
}

/** 출력 ZIP 의 각 시트 XML 에 `<hyperlinks>` 블록 + rels External 관계를 주입한다. */
async function injectHyperlinks(buf: ArrayBuffer, content: XlsxWorkbookModel): Promise<ArrayBuffer> {
  const needs = content.sheets.some((s) => sheetHyperlinkCells(s).length > 0);
  if (!needs) return buf;
  try {
    const zip = await JSZip.loadAsync(buf);
    let mutated = false;
    for (let i = 0; i < content.sheets.length; i++) {
      const links = sheetHyperlinkCells(content.sheets[i]);
      if (links.length === 0) continue;
      const path = `xl/worksheets/sheet${i + 1}.xml`;
      const file = zip.file(path);
      if (!file) continue;
      let xml = await file.async('text');
      if (xml.includes('<hyperlinks')) continue; // 이미 주입됨 — 이중 안전

      const relsPath = `xl/worksheets/_rels/sheet${i + 1}.xml.rels`;
      const relsFile = zip.file(relsPath);
      const relsXml = relsFile ? await relsFile.async('text') : null;
      let relId = nextRelIdNum(relsXml);
      const relParts: string[] = [];
      const tags: string[] = [];

      for (const [a1, hl] of links) {
        const target = hl.target.trim();
        const ref = a1.toUpperCase();
        const tooltip = hl.tooltip ? ` tooltip="${xmlEscape(hl.tooltip)}"` : '';
        if (isExternalHyperlinkTarget(target)) {
          const id = `rId${relId}`;
          relId += 1;
          tags.push(`<hyperlink ref="${xmlEscape(ref)}" r:id="${id}"${tooltip}/>`);
          relParts.push(
            `<Relationship Id="${id}" Type="${HYPERLINK_REL_TYPE}" Target="${xmlEscape(target)}" TargetMode="External"/>`,
          );
        } else {
          // 내부 참조 — rId 없이 location 속성으로 방출
          tags.push(`<hyperlink ref="${xmlEscape(ref)}" location="${xmlEscape(target)}"${tooltip}/>`);
        }
      }
      if (tags.length === 0) continue;

      const insertAt = findInsertPos(xml, HYPERLINK_AFTER_ANCHORS);
      if (insertAt === -1) continue;
      xml = xml.slice(0, insertAt) + `<hyperlinks>${tags.join('')}</hyperlinks>` + xml.slice(insertAt);
      zip.file(path, xml);
      if (relParts.length > 0) zip.file(relsPath, mergeSheetRels(relsXml, relParts));
      mutated = true;
    }
    if (!mutated) return buf;
    return await zip.generateAsync({ type: 'arraybuffer' });
  } catch (err) {
    console.warn('[xlsx-engine] hyperlink injection failed:', err);
    return buf;
  }
}

/* ------------------------------------------------------------------ */
/* 메모(코멘트) 직렬화 — comments{N}.xml + VML 드로잉 + rels + Content_Types */
/*                                                                     */
/* SheetJS 는 셀 메모를 직렬화하지 않으므로, IR 셀의 cell.note 를 모아      */
/*   - xl/comments{N}.xml            : 코멘트 데이터(authors + commentList) */
/*   - xl/drawings/vmlDrawing{N}.vml : 레거시 hover 박스 앵커(셀당 shape)   */
/*   - 시트 rels                     : comments + vmlDrawing 관계           */
/*   - 시트 XML                      : <legacyDrawing r:id=..> 참조         */
/*   - [Content_Types].xml           : comments Override(vml Default 은 이미 */
/*                                     SheetJS 가 선언)                     */
/* 를 생성한다. comments{N}.xml 이 데이터 원본이므로 앱 내 표시·에이전트     */
/* 왕복은 VML 없이도 무손실이며, VML 은 다운로드한 파일을 Excel 데스크톱에서 */
/* 열었을 때 hover 박스를 띄우기 위한 레거시 요구사항이다.                  */
/* ------------------------------------------------------------------ */

/** 시트의 메모 보유 셀 목록(삽입 순서 유지). */
function sheetNoteCells(sheet: XlsxSheet): Array<[string, NonNullable<XlsxCellValue['note']>]> {
  const out: Array<[string, NonNullable<XlsxCellValue['note']>]> = [];
  for (const [a1, cell] of Object.entries(sheet.cells)) {
    if (cell && cell.note && typeof cell.note.text === 'string' && cell.note.text !== '') {
      out.push([a1, cell.note]);
    }
  }
  return out;
}

/** 메모 목록 → comments{N}.xml 본문(authors 중복 제거 + commentList). 테스트용 export. */
export function buildCommentsXml(
  notes: Array<[string, NonNullable<XlsxCellValue['note']>]>,
): string {
  const authors: string[] = [];
  const authorIndex = new Map<string, number>();
  const authorIdOf = (author: string | undefined): number => {
    const a = author ?? '';
    let idx = authorIndex.get(a);
    if (idx === undefined) {
      idx = authors.length;
      authorIndex.set(a, idx);
      authors.push(a);
    }
    return idx;
  };
  const commentTags = notes
    .map(([a1, note]) => {
      const aid = authorIdOf(note.author);
      const ref = a1.toUpperCase();
      return (
        `<comment ref="${xmlEscape(ref)}" authorId="${aid}">` +
        `<text><r><t xml:space="preserve">${xmlEscape(note.text)}</t></r></text>` +
        `</comment>`
      );
    })
    .join('');
  const authorsXml = `<authors>${authors
    .map((a) => `<author>${xmlEscape(a)}</author>`)
    .join('')}</authors>`;
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<comments xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    authorsXml +
    `<commentList>${commentTags}</commentList>` +
    '</comments>'
  );
}

/** 코멘트 1개의 VML shape(레거시 hover 박스). row/col 은 0-based, shapeIndex 는 0-base 순번. */
function commentVmlShape(row: number, col: number, shapeIndex: number): string {
  // idmap data="1" 이므로 shape id 는 1025 부터 시작하는 관례를 따른다.
  const id = `_x0000_s${1025 + shapeIndex}`;
  const z = shapeIndex + 1;
  // Anchor: LeftCol,LeftOff,LeftRow,LeftOff,RightCol,RightOff,RightRow,RightOff — Excel 표준 오프셋.
  const anchor = `${col + 1}, 15, ${row}, 2, ${col + 3}, 15, ${row + 4}, 4`;
  return (
    `<v:shape id="${id}" type="#_x0000_t202" ` +
    `style="position:absolute;margin-left:59.25pt;margin-top:1.5pt;` +
    `width:108pt;height:59.25pt;z-index:${z};visibility:hidden" ` +
    `fillcolor="#ffffe1" o:insetmode="auto">` +
    `<v:fill color2="#ffffe1"/>` +
    `<v:shadow on="t" color="black" obscured="t"/>` +
    `<v:path o:connecttype="none"/>` +
    `<v:textbox style="mso-direction-alt:auto"><div style="text-align:left"></div></v:textbox>` +
    `<x:ClientData ObjectType="Note">` +
    `<x:MoveWithCells/><x:SizeWithCells/>` +
    `<x:Anchor>${anchor}</x:Anchor>` +
    `<x:AutoFill>False</x:AutoFill>` +
    `<x:Row>${row}</x:Row><x:Column>${col}</x:Column>` +
    `</x:ClientData>` +
    `</v:shape>`
  );
}

/** shape 목록 → vmlDrawing{N}.vml 본문(shapelayout + shapetype + shapes). 테스트용 export. */
export function buildVmlDrawing(shapes: string[]): string {
  return (
    '<xml xmlns:v="urn:schemas-microsoft-com:vml" ' +
    'xmlns:o="urn:schemas-microsoft-com:office:office" ' +
    'xmlns:x="urn:schemas-microsoft-com:office:excel">' +
    '<o:shapelayout v:ext="edit"><o:idmap v:ext="edit" data="1"/></o:shapelayout>' +
    '<v:shapetype id="_x0000_t202" coordsize="21600,21600" o:spt="202" ' +
    'path="m,l,21600r21600,l21600,xe"><v:stroke joinstyle="miter"/>' +
    '<v:path gradientshapeok="t" o:connecttype="rect"/></v:shapetype>' +
    shapes.join('') +
    '</xml>'
  );
}

/** 출력 ZIP 에 메모(comments{N}.xml + VML + rels + Content_Types)를 주입한다. */
async function injectComments(buf: ArrayBuffer, content: XlsxWorkbookModel): Promise<ArrayBuffer> {
  const needs = content.sheets.some((s) => sheetNoteCells(s).length > 0);
  if (!needs) return buf;
  try {
    const zip = await JSZip.loadAsync(buf);
    const ctOverrides: string[] = [];
    let partIndex = 0; // 코멘트 있는 시트마다 1,2,.. 로 증가(파트 번호)
    let mutated = false;
    for (let i = 0; i < content.sheets.length; i++) {
      const notes = sheetNoteCells(content.sheets[i]);
      if (notes.length === 0) continue;
      const path = `xl/worksheets/sheet${i + 1}.xml`;
      const file = zip.file(path);
      if (!file) continue;
      let xml = await file.async('text');
      if (xml.includes('<legacyDrawing')) continue; // 이미 주입됨 — 이중 안전

      partIndex += 1;
      const commentsPath = `xl/comments${partIndex}.xml`;
      const vmlPath = `xl/drawings/vmlDrawing${partIndex}.vml`;

      // 1) comments{N}.xml — 데이터 원본
      zip.file(commentsPath, buildCommentsXml(notes));

      // 2) vmlDrawing{N}.vml — 셀당 hover 박스 shape
      const shapes = notes.map(([a1], idx) => {
        const { r, c } = XLSX.utils.decode_cell(a1.toUpperCase());
        return commentVmlShape(r, c, idx);
      });
      zip.file(vmlPath, buildVmlDrawing(shapes));

      // 3) 시트 rels — comments + vmlDrawing 관계
      const relsPath = `xl/worksheets/_rels/sheet${i + 1}.xml.rels`;
      const relsFile = zip.file(relsPath);
      const relsXml = relsFile ? await relsFile.async('text') : null;
      let relId = nextRelIdNum(relsXml);
      const commentsRelId = `rId${relId}`;
      relId += 1;
      const vmlRelId = `rId${relId}`;
      relId += 1;
      zip.file(
        relsPath,
        mergeSheetRels(relsXml, [
          `<Relationship Id="${commentsRelId}" Type="${COMMENTS_REL_TYPE}" Target="../comments${partIndex}.xml"/>`,
          `<Relationship Id="${vmlRelId}" Type="${VML_DRAWING_REL_TYPE}" Target="../drawings/vmlDrawing${partIndex}.vml"/>`,
        ]),
      );

      // 4) 시트 XML — legacyDrawing(VML) 참조
      const insertAt = findInsertPos(xml, LEGACY_DRAWING_AFTER_ANCHORS);
      if (insertAt === -1) continue;
      xml = xml.slice(0, insertAt) + `<legacyDrawing r:id="${vmlRelId}"/>` + xml.slice(insertAt);
      zip.file(path, xml);

      // 5) Content_Types override(코멘트) — vml Default 은 SheetJS 가 이미 선언함
      ctOverrides.push(
        `<Override PartName="/${commentsPath}" ContentType="${COMMENTS_CONTENT_TYPE}"/>`,
      );
      mutated = true;
    }
    if (!mutated) return buf;
    if (ctOverrides.length > 0) {
      const ctFile = zip.file('[Content_Types].xml');
      if (ctFile) {
        let ct = await ctFile.async('text');
        const idx = ct.lastIndexOf('</Types>');
        if (idx !== -1) {
          ct = ct.slice(0, idx) + ctOverrides.join('') + ct.slice(idx);
          zip.file('[Content_Types].xml', ct);
        }
      }
    }
    return await zip.generateAsync({ type: 'arraybuffer' });
  } catch (err) {
    console.warn('[xlsx-engine] comment injection failed:', err);
    return buf;
  }
}

/* ------------------------------------------------------------------ */
/* 출력 패키지 finalizer                                               */
/* ------------------------------------------------------------------ */

/**
 * Normalize SheetJS package artifacts that affect native Excel interoperability.
 *
 * Style-only cells are kept as self-closing `<c r="..." s="..."/>` nodes instead
 * of empty-string values, and workbook calculation is requested on next open.
 */
export async function finalizeXlsxWorkbookPackage(buf: ArrayBuffer): Promise<ArrayBuffer> {
  const zip = await JSZip.loadAsync(buf);
  let mutated = false;

  const worksheetPaths = Object.keys(zip.files).filter((path) =>
    /^xl\/worksheets\/[^/]+\.xml$/.test(path),
  );
  await Promise.all(worksheetPaths.map(async (path) => {
    const file = zip.file(path);
    if (!file) return;
    const original = await file.async('text');
    const cleaned = original.replace(
      /<c\b([^>]*)>\s*<v\s*(?:\/>|>\s*<\/v>)\s*<\/c>/g,
      (whole, attributes: string) => {
        if (!/\bt\s*=\s*(["'])str\1/.test(attributes)) return whole;
        const withoutStringType = attributes.replace(/\s+t\s*=\s*(["'])str\1/, '');
        return `<c${withoutStringType}/>`;
      },
    );
    if (cleaned !== original) {
      zip.file(path, cleaned);
      mutated = true;
    }
  }));

  const workbookFile = zip.file('xl/workbook.xml');
  if (workbookFile) {
    const original = await workbookFile.async('text');
    let updated = original;
    const existing = /<calcPr\b([^>]*?)(\/?)>/.exec(updated);
    if (existing) {
      let attributes = existing[1];
      attributes = setXmlAttribute(attributes, 'fullCalcOnLoad', '1');
      attributes = setXmlAttribute(attributes, 'forceFullCalc', '1');
      if (!/\scalcId\s*=/.test(attributes)) {
        attributes = setXmlAttribute(attributes, 'calcId', '191029');
      }
      updated = updated.replace(existing[0], `<calcPr${attributes}${existing[2]}>`);
    } else {
      const calcPr = '<calcPr calcId="191029" fullCalcOnLoad="1" forceFullCalc="1"/>';
      updated = updated.replace('</workbook>', `${calcPr}</workbook>`);
    }
    if (updated !== original) {
      zip.file('xl/workbook.xml', updated);
      mutated = true;
    }
  }

  return mutated ? zip.generateAsync({ type: 'arraybuffer' }) : buf;
}

/* ------------------------------------------------------------------ */
/* Public: IR → xlsx ArrayBuffer                                        */
/* ------------------------------------------------------------------ */

export interface SerializeXlsxOptions {
  /** 조건부 서식을 바이트에 주입. 호스트 렌더와 다운로드에서 필요할 때 활성화한다. */
  includeConditionalFormats?: boolean;
}

export async function serializeXlsxWorkbook(
  content: XlsxWorkbookModel,
  opts: SerializeXlsxOptions = {},
): Promise<ArrayBuffer> {
  const wb = irToWorkbook(content);
  let out: ArrayBuffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx', cellStyles: true });
  out = await injectShrinkToFitStyles(out, content);
  out = await injectActiveSheetIndex(out, content.activeSheetIndex, wb.SheetNames.length);
  // 조건부서식은 dataValidations 보다 앞서야 하므로 DV 주입 전에 처리한다.
  if (opts.includeConditionalFormats) {
    out = await injectConditionalFormats(out, content);
  }
  out = await injectDataValidations(out, content);
  out = await injectFreezePanes(out, content);
  // 행 개요 레벨 — 부분합이 부여한 outlineLevel 을 <row> 에 후주입
  out = await injectRowOutlineLevels(out, content);
  // 오토필터 열 조건 — SheetJS 가 쓴 ref-only 요소를 조건 포함 형태로 교체
  out = await injectAutoFilterCriteria(out, content);
  // 하이퍼링크(<hyperlinks> + rels) — dataValidations 뒤 위치에 주입
  out = await injectHyperlinks(out, content);
  // 메모(comments{N}.xml + VML + rels + Content_Types) — legacyDrawing 참조
  out = await injectComments(out, content);
  out = await finalizeXlsxWorkbookPackage(out);
  return out;
}
